<script lang="ts" setup>
import AppCard from "@/components/AppCard.vue";
import AppCardContent from "@/components/AppCardContent.vue";
import AppCardFooter from "@/components/AppCardFooter.vue";
import AppCardFooterContent from "@/components/AppCardFooterContent.vue";
import AppCardHeader from "@/components/AppCardHeader.vue";
import AppCardHoverActivator from "@/components/AppCardHoverActivator.vue";
import AppContent from "@/components/AppContent.vue";
import AppTextDescription from "@/components/AppTextDescription.vue";
import AppTextTitle from "@/components/AppTextTitle.vue";
import CharacterActions from "@/components/CharacterActions.vue";
import CharacterThumbnail from "@/components/CharacterThumbnail.vue";
import DokkanCharacterDetails from "@/components/DokkanCharacterDetails.vue";
import useCharacter from "@/composables/character.js";
import { Character } from "@/types/character";
import { Link } from "@inertiajs/vue3";
import { ArrowRight } from "lucide-vue-next";
import { computed, ref } from "vue";
import { route } from "ziggy-js";

interface Props {
  character: Character;
}

const props = defineProps<Props>();

const displayedCharacter = ref(props.character);

/**
 * Updates the displayed character.
 */
const updateDisplayedCharacter = (character: Character) => {
  displayedCharacter.value = {
    ...displayedCharacter.value,
    ...character,
  };
};

const eza = ref(true);
const hiddenPotentialUnlocked = ref(props.character.hidden_potential !== null);
const { hasEza, hasSeza, hp, atk, def, leaderSkill } = useCharacter(
  props.character,
  eza,
  hiddenPotentialUnlocked,
);

const transformationCharacter = computed<Character | null>(
  () => props.character.transformation_character,
);
const transformation = computed<boolean>(
  () => displayedCharacter.value.id === transformationCharacter.value?.id,
);
</script>

<template>
  <AppCard>
    <AppCardHeader>
      <div class="flex w-full flex-col overflow-hidden">
        <AppTextDescription v-if="character.leader_skill" class="truncate"
          >{{ character.leader_skill.name }}
        </AppTextDescription>

        <AppTextTitle prepended truncated>
          <Link
            :data-card-hover="true"
            :href="route('characters.show', displayedCharacter.id)"
            >{{ displayedCharacter.name }}
          </Link>
        </AppTextTitle>
      </div>

      <CharacterActions
        v-model:eza="eza"
        :base-character="character"
        :character="displayedCharacter"
        :inline="true"
        :transformation="transformation"
        class="flex-col items-end"
        @update-character="updateDisplayedCharacter"
      />
    </AppCardHeader>

    <AppCardContent
      :class="{
        '!from-blue-600/10': eza && hasEza && !hasSeza,
        '!from-fuchsia-600/10': eza && hasSeza,
        '!to-red-600/10': transformation,
      }"
      class="h-full bg-gradient-to-tr from-transparent to-transparent"
    >
      <div class="flex gap-4">
        <div class="flex w-full flex-col gap-4">
          <div class="flex w-full gap-4">
            <AppContent
              v-for="(amount, stat) in { hp, atk, def }"
              :key="stat"
              :heading="{ class: '!text-base uppercase' }"
              class="basis-1/3"
            >
              <template #heading>
                {{ stat }}
              </template>

              <span class="text-lg font-extrabold">{{ amount }}</span>
            </AppContent>
          </div>
        </div>

        <AppCardHoverActivator class="ml-auto mr-2 mt-2">
          <CharacterThumbnail
            :character="displayedCharacter"
            :potential-unlocked="hiddenPotentialUnlocked"
            @toggle-hidden-potential="
              hiddenPotentialUnlocked = !hiddenPotentialUnlocked
            "
          />
        </AppCardHoverActivator>
      </div>

      <AppContent v-if="character.leader_skill">
        <template #heading>Leader Skill</template>

        {{ leaderSkill.description }}
      </AppContent>

      <DokkanCharacterDetails :character="character" class="mt-auto" />
    </AppCardContent>

    <AppCardFooter>
      <AppCardHoverActivator
        :href="
          route('characters.show', {
            character: character.id,
          })
        "
      >
        <AppCardFooterContent>
          Show Full Details &amp; Link Partners
          <ArrowRight />
        </AppCardFooterContent>
      </AppCardHoverActivator>
    </AppCardFooter>
  </AppCard>
</template>

<style scoped></style>
